// src/pages/monitoring/submissions/FieldSubmissionList.jsx
/**
 * Field Submission List — v2 dengan RBAC.
 *
 * RBAC RULES:
 *   SUPERVISOR  → data difilter server-side (assignments mereka saja)
 *                 → tombol Submit Progress & Upload Evidence TAMPIL
 *                 → tombol Accept/Override SEMBUNYI
 *
 *   PM/PC/ADMIN → semua data tampil
 *                 → tombol Submit & Upload TAMPIL (secondary — untuk upload dokumen)
 *                 → tombol Review Submission TAMPIL
 *
 * PRESELECTED SCOPE:
 *   Tombol di header halaman → modal 3 step (pilih segment dulu)
 *   Tombol di segment card   → modal 2 step (segment sudah terpilih, langsung milestone)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, AlertTriangle, ChevronDown, ChevronUp,
  ArrowRight, FileText, TrendingUp, Upload, Search,
} from 'lucide-react';
import toast from 'react-hot-toast';

import HasRole from '../../../components/auth/HasRole';
import {
  getAllSubmissions,
  groupSubmissionsByScope,
  groupByMilestone,
  computeScopeSummary,
  ProgressStatus,
  PENDING_STATUSES,
} from '../../../services/progressService';

import SubmitProgressModal from './SubmitProgressModal';
import UploadEvidenceModal from './UploadEvidenceModal';

// ============================================================================
// HELPER — konversi group object → preselectedScope format
// ============================================================================

/**
 * group dari groupSubmissionsByScope() punya format:
 *   { scopeType: 'SEGMENT'|'SITE', scopeId, scopeName, scopeCode, projectName }
 *
 * preselectedScope di modal butuh format:
 *   { id, name, code, projectName, scopeType: 'FO'|'TOWER' }
 */
const buildPreselectedScope = (group) => {
  if (!group) return null;
  return {
    id:          group.scopeId,
    name:        group.scopeName,
    code:        group.scopeCode,
    projectName: group.projectName,
    scopeType:   group.scopeType === 'SEGMENT' ? 'FO' : 'TOWER',
  };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const TypePill = ({ type }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
    type === 'SEGMENT' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
    {type === 'SEGMENT' ? 'FO' : 'Tower'}
  </span>
);

const StatusPill = ({ label, count, variant }) => {
  if (!count) return null;
  const cls = {
    pending:  'bg-amber-50 border-amber-200 text-amber-700',
    evidence: 'bg-purple-50 border-purple-200 text-purple-700',
    approved: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    rejected: 'bg-red-50 border-red-200 text-red-700',
  }[variant] ?? 'bg-gray-50 border-gray-200 text-gray-600';

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${cls}`}>
      {count} {label}
    </span>
  );
};

const MilestoneRow = ({ milestoneGroup, onNavigate }) => {
  const subs         = milestoneGroup.submissions;
  const latestSub    = subs[0];
  const pendingCount = subs.filter(s => PENDING_STATUSES.includes(s.status)).length;
  const evPending    = subs.reduce((a, s) => a + (s.evidencePendingCount ?? 0), 0);

  return (
    <div
      className="flex items-center justify-between px-4 py-3 border-b last:border-b-0
        border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={onNavigate}>
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-mono flex-shrink-0">
            {milestoneGroup.milestoneItemCode}
          </span>
          <span className="text-sm font-medium text-gray-800 truncate">
            {milestoneGroup.milestoneName}
          </span>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {pendingCount > 0 && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
              bg-amber-50 border border-amber-200 text-amber-700 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
              PROGRESS · {pendingCount} pending
            </span>
          )}
          {pendingCount === 0 && subs.some(s => s.status === ProgressStatus.APPROVED) && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
              bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
              approved
            </span>
          )}
          {evPending > 0 && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full
              bg-purple-50 border border-purple-200 text-purple-700 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block" />
              EVIDENCE · {evPending} pending
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
        {latestSub && (
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full"
                style={{ width: `${latestSub.submittedProgress ?? 0}%` }} />
            </div>
            <span className="text-xs text-gray-500 w-8 text-right">
              {latestSub.submittedProgress ?? 0}%
            </span>
          </div>
        )}
        <ArrowRight className="w-4 h-4 text-gray-300" />
      </div>
    </div>
  );
};

/**
 * SegmentBlock
 *
 * onSubmitProgress(group) — dipanggil dengan group object agar modal
 *   tahu segment mana yang sudah dipilih (preselectedScope).
 * onUploadEvidence(group) — sama.
 */
const SegmentBlock = ({ group, onNavigate, onSubmitProgress, onUploadEvidence }) => {
  const [expanded, setExpanded] = useState(true);

  const summary = useMemo(
    () => computeScopeSummary(group.submissions),
    [group.submissions]
  );
  const milestoneGroups = useMemo(
    () => Array.from(groupByMilestone(group.submissions).values()),
    [group.submissions]
  );

  const hasPending = summary.pendingProgress > 0 || summary.evidencePending > 0;

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${
      hasPending ? 'border-amber-200' : 'border-gray-200'}`}>

      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer
          select-none transition-colors ${
          hasPending
            ? 'bg-amber-50 hover:bg-amber-100/50'
            : 'bg-gray-50 hover:bg-gray-100/50'}`}
        onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-2 min-w-0">
          <TypePill type={group.scopeType} />
          <span className="text-sm font-semibold text-gray-900 truncate">
            {group.scopeName}
          </span>
          <span className="text-xs text-gray-400 hidden sm:inline truncate">
            {group.scopeCode}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <StatusPill label="pending"    count={summary.pendingProgress}  variant="pending"  />
          <StatusPill label="ev.pending" count={summary.evidencePending}  variant="evidence" />
          <StatusPill label="approved"   count={summary.approvedProgress} variant="approved" />
          {expanded
            ? <ChevronUp   className="w-4 h-4 text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <>
          {/* Milestone rows */}
          {milestoneGroups.map(mg => (
            <MilestoneRow
              key={mg.milestoneId}
              milestoneGroup={mg}
              onNavigate={() => onNavigate(group)}
            />
          ))}

          {/* Footer actions */}
          <div className={`px-4 py-2.5 border-t flex items-center gap-2 flex-wrap ${
            hasPending ? 'border-amber-100' : 'border-gray-100'}`}>

            {/* Field worker: submit & upload — pass group untuk preselect */}
            <HasRole isFieldWorker={true}>
              <button
                onClick={e => { e.stopPropagation(); onSubmitProgress(group); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                  bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200
                  rounded-lg transition-colors">
                <TrendingUp className="w-3.5 h-3.5" />
                Submit Progress
              </button>
              <button
                onClick={e => { e.stopPropagation(); onUploadEvidence(group); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                  bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200
                  rounded-lg transition-colors">
                <Upload className="w-3.5 h-3.5" />
                Upload Evidence
              </button>
            </HasRole>

            {/* Manager: Review button */}
            <HasRole isManager={true}>
              {hasPending ? (
                <button
                  onClick={() => onNavigate(group)}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-blue-900
                    hover:bg-blue-800 text-white text-xs font-medium rounded-lg
                    transition-colors">
                  <FileText className="w-3.5 h-3.5" />
                  Review Submission
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  onClick={() => onNavigate(group)}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-gray-100
                    hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg
                    transition-colors">
                  Lihat Detail
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </HasRole>

            {/* Manager: secondary submit/upload icon buttons — pass group untuk preselect */}
            <HasRole isManager={true}>
              <button
                onClick={e => { e.stopPropagation(); onSubmitProgress(group); }}
                title="Submit Progress"
                className="flex items-center px-2.5 py-1.5 text-xs text-gray-500
                  hover:text-blue-600 border border-gray-200 hover:border-blue-200
                  rounded-lg transition-colors bg-white">
                <TrendingUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={e => { e.stopPropagation(); onUploadEvidence(group); }}
                title="Upload Evidence"
                className="flex items-center px-2.5 py-1.5 text-xs text-gray-500
                  hover:text-purple-600 border border-gray-200 hover:border-purple-200
                  rounded-lg transition-colors bg-white">
                <Upload className="w-3.5 h-3.5" />
              </button>
            </HasRole>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// EMPTY SCOPE CARD — tidak pass scope (user belum tahu mau ke segment mana)
// ============================================================================

const EmptyScopeCard = ({ onSubmitProgress, onUploadEvidence }) => (
  <div className="bg-white border border-dashed border-gray-300 rounded-xl p-5
    flex items-center justify-between">
    <div>
      <p className="text-sm text-gray-500">Belum ada submission</p>
      <p className="text-xs text-gray-400 mt-0.5">
        Gunakan tombol di kanan untuk memulai
      </p>
    </div>
    <div className="flex gap-2">
      <HasRole isFieldWorker={true}>
        <button
          onClick={onSubmitProgress}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
            bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200
            rounded-lg transition-colors">
          <TrendingUp className="w-3.5 h-3.5" />
          Submit Progress
        </button>
        <button
          onClick={onUploadEvidence}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
            bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200
            rounded-lg transition-colors">
          <Upload className="w-3.5 h-3.5" />
          Upload Evidence
        </button>
      </HasRole>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FieldSubmissionList() {
  const navigate = useNavigate();

  const [submissions,  setSubmissions]  = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterScope,  setFilterScope]  = useState('ALL');
  const [searchQuery,  setSearchQuery]  = useState('');

  // Modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);

  // Preselected scope — null = global (3 steps), object = dari segment card (2 steps)
  const [preselectedScope, setPreselectedScope] = useState(null);

  // ── Load ────────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllSubmissions();
      setSubmissions(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal memuat submissions');
      toast.error('Gagal memuat data submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Modal handlers ───────────────────────────────────────────────────────────

  /**
   * Buka modal Submit Progress.
   * @param {object|null} group — bila dari segment card, pass group object.
   *   Bila dari header (global), tidak pass group (atau pass null).
   */
  const openProgressModal = (group = null) => {
    setPreselectedScope(buildPreselectedScope(group));
    setShowProgressModal(true);
  };

  const closeProgressModal = () => {
    setShowProgressModal(false);
    setPreselectedScope(null);
  };

  /**
   * Buka modal Upload Evidence.
   * @param {object|null} group — sama dengan openProgressModal.
   */
  const openEvidenceModal = (group = null) => {
    setPreselectedScope(buildPreselectedScope(group));
    setShowEvidenceModal(true);
  };

  const closeEvidenceModal = () => {
    setShowEvidenceModal(false);
    setPreselectedScope(null);
  };

  // ── Navigate ────────────────────────────────────────────────────────────────

  const handleNavigate = (group) => {
    const path = group.scopeType === 'SEGMENT'
      ? `/monitoring/submissions/segment/${group.scopeId}`
      : `/monitoring/submissions/site/${group.scopeId}`;
    navigate(path, {
      state: {
        scopeName:   group.scopeName,
        scopeCode:   group.scopeCode,
        projectName: group.projectName,
      },
    });
  };

  // ── Stats ───────────────────────────────────────────────────────────────────

  const totalPending   = submissions.filter(s => PENDING_STATUSES.includes(s.status)).length;
  const totalApproved  = submissions.filter(s => s.status === ProgressStatus.APPROVED).length;
  const totalRejected  = submissions.filter(s => s.status === ProgressStatus.REJECTED).length;
  const totalEvPending = submissions.reduce((a, s) => a + (s.evidencePendingCount ?? 0), 0);

  // ── Filter + Group ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => submissions.filter(s => {
    const matchStatus = filterStatus === 'ALL'
      ? true
      : filterStatus === 'PENDING'
        ? PENDING_STATUSES.includes(s.status)
        : s.status === filterStatus;

    const matchScope = filterScope === 'ALL' || s.scopeType === filterScope;

    const q = searchQuery.toLowerCase();
    const matchSearch = !q
      || (s.scopeName       ?? '').toLowerCase().includes(q)
      || (s.scopeCode       ?? '').toLowerCase().includes(q)
      || (s.milestoneName   ?? '').toLowerCase().includes(q)
      || (s.submittedByName ?? '').toLowerCase().includes(q)
      || (s.projectName     ?? '').toLowerCase().includes(q);

    return matchStatus && matchScope && matchSearch;
  }), [submissions, filterStatus, filterScope, searchQuery]);

  const scopeGroups = useMemo(
    () => Array.from(groupSubmissionsByScope(filtered).values()),
    [filtered]
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full space-y-5">

      {/* Page header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900
        rounded-2xl p-6 shadow-xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-6 bg-red-500 rounded-full" />
              <span className="text-blue-200 text-xs font-semibold uppercase tracking-widest">
                Field Monitoring
              </span>
            </div>
            <h1 className="text-white text-2xl font-bold">Field Submissions</h1>
            <p className="text-blue-300 text-sm mt-1">
              Progress &amp; evidence dikelompokkan per segment / site
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20
              text-white border border-white/20 rounded-xl px-4 py-2 text-sm
              font-medium transition-all disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[
            { label: 'Progress Pending', value: totalPending,   color: 'from-amber-600 to-amber-500',    pulse: true },
            { label: 'Evidence Pending', value: totalEvPending, color: 'from-orange-600 to-orange-500' },
            { label: 'Approved',         value: totalApproved,  color: 'from-emerald-700 to-emerald-600' },
            { label: 'Rejected',         value: totalRejected,  color: 'from-red-700 to-red-600' },
          ].map(({ label, value, color, pulse }) => (
            <div key={label}
              className={`bg-gradient-to-br ${color} rounded-xl px-4 py-3 text-white relative`}>
              <p className="text-xs opacity-70">{label}</p>
              <p className="text-2xl font-bold mt-0.5">{value}</p>
              {pulse && value > 0 && (
                <div className="absolute top-2 right-2 w-2 h-2 bg-white
                  rounded-full animate-pulse" />
              )}
            </div>
          ))}
        </div>

        {/* Global action buttons — TIDAK pass group (3 steps modal) */}
        <div className="flex gap-2 flex-wrap">
          <HasRole isFieldWorker={true}>
            <button
              onClick={() => openProgressModal()}
              className="flex items-center gap-2 bg-white text-blue-900 font-medium
                px-4 py-2 rounded-xl text-sm hover:bg-blue-50 transition-colors">
              <TrendingUp className="w-4 h-4" />
              Submit Progress
            </button>
            <button
              onClick={() => openEvidenceModal()}
              className="flex items-center gap-2 bg-white/15 text-white font-medium
                px-4 py-2 rounded-xl text-sm hover:bg-white/25 border border-white/20
                transition-colors">
              <Upload className="w-4 h-4" />
              Upload Evidence
            </button>
          </HasRole>

          <HasRole isManager={true}>
            <button
              onClick={() => openProgressModal()}
              className="flex items-center gap-2 bg-white/10 text-white/80 font-medium
                px-3 py-2 rounded-xl text-xs hover:bg-white/20 border border-white/15
                transition-colors">
              <TrendingUp className="w-3.5 h-3.5" />
              Submit Progress
            </button>
            <button
              onClick={() => openEvidenceModal()}
              className="flex items-center gap-2 bg-white/10 text-white/80 font-medium
                px-3 py-2 rounded-xl text-xs hover:bg-white/20 border border-white/15
                transition-colors">
              <Upload className="w-3.5 h-3.5" />
              Upload Evidence
            </button>
          </HasRole>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200
          rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={load}
            className="text-red-600 text-sm font-medium hover:underline">
            Retry
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex
        flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Cari supervisor, segment, milestone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg
              text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { key: 'ALL',      label: 'Semua' },
            { key: 'PENDING',  label: `Pending (${totalPending})` },
            { key: 'APPROVED', label: `Approved (${totalApproved})` },
            { key: 'REJECTED', label: `Rejected (${totalRejected})` },
          ].map(f => (
            <button key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                filterStatus === f.key
                  ? 'bg-blue-900 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { key: 'ALL',     label: 'Semua' },
            { key: 'SEGMENT', label: 'FO' },
            { key: 'SITE',    label: 'Tower' },
          ].map(f => (
            <button key={f.key}
              onClick={() => setFilterScope(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                filterScope === f.key
                  ? 'bg-red-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>

        <span className="text-xs text-gray-400 ml-auto">
          {scopeGroups.length} segment/site · {filtered.length} submissions
        </span>
      </div>

      {/* Pending alert */}
      {totalPending > 0 && filterStatus === 'ALL' && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200
          rounded-xl px-5 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800 flex-1">
            <strong>{totalPending} progress</strong> menunggu review PM/PC.
            {totalEvPending > 0 && ` · ${totalEvPending} evidence perlu di-review.`}
          </p>
          <HasRole isManager={true}>
            <button
              onClick={() => setFilterStatus('PENDING')}
              className="bg-amber-500 hover:bg-amber-600 text-white text-xs
                font-semibold px-4 py-2 rounded-lg flex-shrink-0">
              Lihat Pending
            </button>
          </HasRole>
        </div>
      )}

      {/* Group list */}
      {loading && scopeGroups.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl py-16 text-center">
          <RefreshCw className="w-10 h-10 text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-gray-400">Loading submissions...</p>
        </div>
      ) : scopeGroups.length > 0 ? (
        <div className="space-y-4">
          {scopeGroups.map(group => (
            <SegmentBlock
              key={`${group.scopeType}_${group.scopeId}`}
              group={group}
              onNavigate={handleNavigate}
              // Pass group → modal akan skip step 1 (preselectedScope)
              onSubmitProgress={(grp) => openProgressModal(grp)}
              onUploadEvidence={(grp) => openEvidenceModal(grp)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl py-12 text-center">
            <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 font-medium">Tidak ada submission ditemukan</p>
            {(searchQuery || filterStatus !== 'ALL' || filterScope !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterStatus('ALL');
                  setFilterScope('ALL');
                }}
                className="mt-3 text-blue-600 text-sm hover:underline">
                Reset filter
              </button>
            )}
          </div>

          {/* Empty state — tidak pass scope (user belum tahu mau ke segment mana) */}
          <HasRole isFieldWorker={true}>
            <EmptyScopeCard
              onSubmitProgress={() => openProgressModal()}
              onUploadEvidence={() => openEvidenceModal()}
            />
          </HasRole>
        </div>
      )}

      {/* Modals */}
      {showProgressModal && (
        <SubmitProgressModal
          onClose={closeProgressModal}
          onSuccess={load}
          preselectedScope={preselectedScope}
        />
      )}
      {showEvidenceModal && (
        <UploadEvidenceModal
          onClose={closeEvidenceModal}
          onSuccess={load}
          preselectedScope={preselectedScope}
        />
      )}
    </div>
  );
}